import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getSearchHistory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { STATUS_CONFIG } from '../components/CompanyCard';

const SearchHistoryPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [historyItems, setHistoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingTaskId, setLoadingTaskId] = useState(null);
  const [copiedTaskId, setCopiedTaskId] = useState(null);
  const navigate = useNavigate();

  const loadSearchHistory = async () => {
    try {
      setLoading(true);
      setError('');
      
      const history = await getSearchHistory();
      const uniqueHistory = history.filter(
        (item, index, self) => index === self.findIndex(t => t.task_id === item.task_id)
      );
      setHistoryItems(uniqueHistory);
    } catch (err) {
      setError('Ошибка загрузки истории поисков. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTaskId = (taskId, event) => {
    event.stopPropagation(); // Предотвращаем клик по элементу списка
    navigator.clipboard.writeText(taskId).then(() => {
      setCopiedTaskId(taskId);
      setTimeout(() => setCopiedTaskId(null), 2000); // Сбрасываем через 2 секунды
    }).catch(err => {
      console.error('Ошибка копирования:', err);
    });
  };

  useEffect(() => {
    if (authLoading || !user) {
      setLoading(false);
      return;
    }
    loadSearchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const handleHistoryItemClick = async (item) => {
    if (loadingTaskId) return;
    
    setLoadingTaskId(item.task_id);
    setError('');
    
    sessionStorage.setItem('viewing_history_task_id', item.task_id);
    navigate(`/results/${item.task_id}?from=history`);
    
    setLoadingTaskId(null);
  };

  const formatRelativeTime = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffHours < 1) {
        return 'Меньше часа назад';
      } else if (diffHours < 24) {
        const hourForm = diffHours % 10 === 1 && diffHours % 100 !== 11 ? 'час'
          : diffHours % 10 >= 2 && diffHours % 10 <= 4 && (diffHours % 100 < 10 || diffHours % 100 >= 20) ? 'часа'
          : 'часов';
        return `${diffHours} ${hourForm} назад`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? 'день' : diffDays < 5 ? 'дня' : 'дней'} назад`;
      } else {
        return date.toLocaleDateString('ru-RU');
      }
    } catch {
      return 'Неизвестно';
    }
  };

  const renderStatusChips = (statusStats) => {
    if (!statusStats) return null;

    const chips = [];
    
    // Новые (без статуса)
    if (statusStats.no_status > 0) {
      chips.push(
        <Chip
          key="new"
          label={`🆕 ${statusStats.no_status}`}
          size="small"
          sx={{ 
            height: 22, 
            fontSize: '0.75rem',
            bgcolor: '#e3f2fd',
            color: '#1565c0'
          }}
        />
      );
    }

    // Статусы из конфига
    Object.entries(STATUS_CONFIG).forEach(([key, config]) => {
      const count = statusStats[key];
      if (count > 0) {
        chips.push(
          <Chip
            key={key}
            label={`${config.icon} ${count}`}
            size="small"
            sx={{ 
              height: 22, 
              fontSize: '0.75rem',
              bgcolor: config.bgColor,
              color: config.color
            }}
          />
        );
      }
    });

    // Если нет статусов совсем — показываем общее количество как новые
    if (chips.length === 0 && statusStats.total > 0) {
      chips.push(
        <Chip
          key="all-new"
          label={`🆕 ${statusStats.total}`}
          size="small"
          sx={{ height: 22, fontSize: '0.75rem', bgcolor: '#e3f2fd', color: '#1565c0' }}
        />
      );
    }

    return <Box sx={{ display: 'inline-flex', gap: 0.5, ml: 1 }}>{chips}</Box>;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/')}
            sx={{ mb: 2 }}
          >
            Вернуться к поиску
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Вернуться к поиску
        </Button>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            История поисков
          </Typography>
          <Tooltip title="Обновить">
            <IconButton onClick={loadSearchHistory} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Все ваши завершенные поиски компаний. Нажмите на элемент, чтобы посмотреть результаты.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {historyItems.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            История поисков пуста
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Выполните поиск компаний, чтобы увидеть их здесь
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          <List disablePadding>
            {historyItems.map((item, index) => (
              <React.Fragment key={item.task_id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleHistoryItemClick(item)}
                    disabled={loadingTaskId === item.task_id}
                    sx={{
                      py: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                      opacity: loadingTaskId === item.task_id ? 0.6 : 1,
                    }}
                  >
                    <ListItemIcon>
                      {loadingTaskId === item.task_id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <BusinessIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography variant="h6" component="span">
                            {item.description}
                          </Typography>
                          {renderStatusChips(item.status_stats)}
                        </Box>
                      }
                      secondary={
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary" component="span">
                                {item.formatted_date}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary" component="span">
                                {formatRelativeTime(item.completed_at)}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 'auto' }}>
                              {item.total_companies} компаний
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Typography variant="caption" color="text.disabled" component="span">
                              ID: {item.task_id}
                            </Typography>
                            <Tooltip title={copiedTaskId === item.task_id ? "Скопировано!" : "Скопировать ID"}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleCopyTaskId(item.task_id, e)}
                                sx={{ 
                                  p: 0.25, 
                                  ml: 0.5,
                                  color: copiedTaskId === item.task_id ? 'success.main' : 'inherit',
                                  '&:hover': { 
                                    color: copiedTaskId === item.task_id ? 'success.main' : 'primary.main' 
                                  } 
                                }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </>
                      }
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                </ListItem>
                {index < historyItems.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Container>
  );
};

export default SearchHistoryPage;
